import { useState } from 'react'

interface AddPositionFormProps {
  accountSlug: string
  onSubmit: (position: any) => void
  onCancel: () => void
}

export function AddPositionForm({ accountSlug, onSubmit, onCancel }: AddPositionFormProps) {
  const [form, setForm] = useState({
    ticker: '',
    quantity: '',
    costBasis: '',
    acquisitionDate: new Date().toISOString().split('T')[0],
  })
  const [error, setError] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!form.ticker || !form.quantity || !form.costBasis || !form.acquisitionDate) {
      setError('All fields are required')
      return
    }

    const quantity = parseInt(form.quantity)
    const costBasis = parseFloat(form.costBasis)

    if (quantity <= 0) {
      setError('Shares must be greater than 0')
      return
    }
    if (costBasis <= 0) {
      setError('Cost basis must be greater than 0')
      return
    }

    onSubmit({
      ticker: form.ticker.toUpperCase(),
      quantity,
      costBasis,
      acquisitionDate: form.acquisitionDate,
      accountSlug,
      sourceTradeId: '',
      status: 'open',
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6" onClick={onCancel}>
      <div className="bg-white rounded-2xl p-10 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-[var(--color-text-primary)]">Add Position</h2>
        <p className="text-sm text-[var(--color-text-muted)] mt-1">Manually add a share position</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Ticker</label>
            <input
              type="text"
              value={form.ticker}
              onChange={(e) => setForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))}
              placeholder="NVDA"
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:outline-none"
              style={{ height: '40px' }}
            />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Shares</label>
              <input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm(f => ({ ...f, quantity: e.target.value }))}
                placeholder="100"
                className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:outline-none"
                style={{ height: '40px' }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Cost Basis / Share</label>
              <input
                type="number"
                step="0.01"
                value={form.costBasis}
                onChange={(e) => setForm(f => ({ ...f, costBasis: e.target.value }))}
                placeholder="180.00"
                className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:outline-none"
                style={{ height: '40px' }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Acquisition Date</label>
            <input
              type="date"
              value={form.acquisitionDate}
              onChange={(e) => setForm(f => ({ ...f, acquisitionDate: e.target.value }))}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white text-[var(--color-text-primary)] focus:border-[var(--color-accent)] focus:outline-none"
              style={{ height: '40px' }}
            />
          </div>

          {error && <p className="text-sm text-[var(--color-red)]">{error}</p>}

          <div className="flex justify-end gap-3 pt-6 mt-2 border-t border-[var(--color-border)]">
            <button type="button" onClick={onCancel} className="px-6 py-2.5 rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]" style={{ height: '44px' }}>Cancel</button>
            <button type="submit" className="px-6 py-2.5 rounded-lg bg-[var(--color-accent)] text-white font-medium hover:bg-[var(--color-accent)]/80" style={{ height: '44px' }}>Add Position</button>
          </div>
        </form>
      </div>
    </div>
  )
}
